import * as React from "react";
import * as moment from "moment";
import { connect } from "react-redux";
import { Tab, Button, Grid, Modal, List, ListItem, Menu } from "semantic-ui-react";

import { FileUpload } from "@teamprevent/common/fileUploads";
import { ConfirmedButton } from "@teamprevent/common/confirmedButton/ConfirmedButton";
import { CurrentUser } from "@teamprevent/common/auth/roles";

import { ApplicationState } from "../../../../applicationState";
import { routeIdSelector as customerIdSelector } from "../../../customer/selectors";
import { routeIdSelector as employeeRecordIdSelector, employeeRecord as employeeRecordSelector } from "../../selectors";
import { EmployeeDocument } from "../../model";
import { deleteEmployeeDocument, createEmployeeDocumentSuccess, loadEmployeeDocuments } from "../../actions";

import { DocumentsTabLabel } from "./DocumentsTabLabel";

interface StateProps {
    employeeRecordId: string;
    customerId: string;
    documents: EmployeeDocument[] | undefined;
    currentUser: CurrentUser;
}

interface DispatchProps {
    deleteDocument: (customerId: string, employeeId: string, employeeDocumentId: string) => Promise<void>;
    createDocumentSuccess: (customerId: string, employeeId: string, employeeDocument: EmployeeDocument) => Promise<void>;
    getDocuments: (customerId: string, employeeId: string) => void;
}

interface State {
    fileUploadKey: number;
    addingFile: boolean;
}

type MergedProps = StateProps & {
    deleteDocument: (employeeDocumentId: string) => Promise<void>;
    createDocumentSuccess: (employeeDocument: EmployeeDocument) => Promise<void>;
    getDocuments: (customerId: string, employeeId: string) => void;
};

const documentTypes = [
    { name: "Clinical Notes" },
    { name: "Form" },
    { name: "General" },
    { name: "Report" }
];

class DocumentsUnconnected extends React.Component<MergedProps, State> {
    constructor(props: MergedProps) {
        super(props);
        this.state = { fileUploadKey: 0, addingFile: false };
        this.documentUploaded = this.documentUploaded.bind(this);
        props.getDocuments(props.customerId, props.employeeRecordId);
    }

    public render() {

        const documents = (this.props.documents ?? []).sort((d1, d2) => (d1.dateCreated && d2.dateCreated) ? -d1.dateCreated.diff(d2.dateCreated) : 0);

        return (
            <Grid columns="4">
                <Grid.Row>
                    <Grid.Column>
                        {/* eslint-disable-next-line react/jsx-no-bind */}
                        <Button content="Add Document" onClick={() => this.setState({ addingFile: true })} />
                        <Modal open={this.state.addingFile}>
                            <Modal.Header>
                                Please select the type of document to upload
                            </Modal.Header>
                            <Modal.Content>
                                <List horizontal>
                                    {documentTypes.map(t =>
                                        (<ListItem key={`${t.name}-${this.state.fileUploadKey}`}>
                                            <FileUpload
                                                // eslint-disable-next-line max-len
                                                apiUrl={`/api/customer/${this.props.customerId}/employeerecord/${this.props.employeeRecordId}/document?documentType=${encodeURIComponent(t.name)}`}
                                                // eslint-disable-next-line react/jsx-no-bind
                                                onFileChange={(documentId, fileName) => this.documentUploaded(documentId, fileName, t.name)}
                                                label={t.name}
                                                acceptExtensions={[
                                                    "avi", "bmp", "csv", "dat", "doc", "docx", "eml", "gif", "jpeg", "jpg", "log", "mht", "mp3", "mp4", "mpeg",
                                                    "mpg", "mpp", "msg", "pdf", "png", "pps", "ppsx", "ptm", "pub", "rar", "rtf", "tif", "tiff", "txt", "vsd",
                                                    "wav", "wma", "wmv", "xls", "xlsx", "xml", "xps", "xsd", "xslt", "zip"
                                                ]}
                                            />
                                        </ListItem>)
                                    )}
                                </List>
                            </Modal.Content>
                            <Modal.Actions>
                                {/* eslint-disable-next-line react/jsx-no-bind */}
                                <Button content="Cancel" onClick={() => this.setState({ addingFile: false })} />
                            </Modal.Actions>
                        </Modal>
                    </Grid.Column>
                </Grid.Row>
                {documents.map((document, index) => {
                    // eslint-disable-next-line max-len
                    const downloadUrl = `/api/customer/${this.props.customerId}/employeerecord/${this.props.employeeRecordId}/document/${document.id}?fileName=${document.name}`;
                    return (
                        <Grid.Row key={index}>
                            <Grid.Column width={4} verticalAlign="middle">
                                <a href={downloadUrl}>
                                    <Button content={document.name} type="button" fluid />
                                </a>
                            </Grid.Column>
                            <Grid.Column width={2} verticalAlign="middle">
                                {document.documentType}
                            </Grid.Column>
                            <Grid.Column width={3} verticalAlign="middle">
                                <span title={document.dateCreated?.toString()}>
                                    {document.dateCreated && document.dateCreated.local().format("DD/MM/yyyy HH:mm")}
                                </span>
                            </Grid.Column>
                            <Grid.Column width={4} verticalAlign="middle">
                                {document.completedBy?.name && <>Uploaded by: {document.completedBy?.name }</>}
                            </Grid.Column>
                            <Grid.Column width={3} verticalAlign="middle">
                                <ConfirmedButton
                                    icon="delete"
                                    className="warning"
                                    content="Delete"
                                    floated="left"
                                    // eslint-disable-next-line react/jsx-no-bind
                                    onClick={() => this.deleteDocument(document.id)}
                                    promptText="Are you sure you want to delete this document? Type 'DELETE' and click confirm to delete."
                                    typeToConfirmText="DELETE"
                                    headingText="Delete Employee Document"
                                />
                            </Grid.Column>
                        </Grid.Row>
                    );
                })}

            </Grid>
        );
    }

    private deleteDocument = (employeeDocumentId: string) => {
        this.props.deleteDocument(employeeDocumentId);
    };

    private documentUploaded = (employeeDocumentId: string, fileName: string, documentType: string) => {
        this.props.createDocumentSuccess({
            id: employeeDocumentId,
            name: fileName,
            documentType,
            dateCreated: moment.utc(),
            completedBy: {
                id: this.props.currentUser.id!,
                name: this.props.currentUser.name
            }
        });
        this.setState({ fileUploadKey: this.state.fileUploadKey + 1, addingFile: false });
    };
}

const mapStateToProps = (state: ApplicationState): StateProps => {
    const employeeRecord = employeeRecordSelector(state);
    return {
        employeeRecordId: employeeRecordIdSelector(state),
        customerId: customerIdSelector(state),
        documents: employeeRecord && "documents" in employeeRecord ? employeeRecord.documents : [],
        currentUser: state.currentUser
    };
};

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
    deleteDocument: (customerId: string, employeeRecordId: string, employeeDocumentId: string) =>
        dispatch(deleteEmployeeDocument(customerId, employeeRecordId, employeeDocumentId)),
    createDocumentSuccess: (customerId: string, employeeRecordId: string, employeeDocument: EmployeeDocument) =>
        dispatch(createEmployeeDocumentSuccess({ employeeRecordId, customerId, document: employeeDocument })),
    getDocuments: (customerId: string, employeeRecordId: string) =>
        dispatch(loadEmployeeDocuments({ customerId, employeeRecordId }))
});

const mergeProps = (stateProps: StateProps, dispatchProps: DispatchProps): MergedProps => ({
    ...stateProps,
    ...dispatchProps,
    deleteDocument: (employeeDocumentId: string) => dispatchProps.deleteDocument(stateProps.customerId, stateProps.employeeRecordId, employeeDocumentId),
    createDocumentSuccess: (employeeDocument: EmployeeDocument) => dispatchProps.createDocumentSuccess(
        stateProps.customerId,
        stateProps.employeeRecordId,
        employeeDocument
    ),
    getDocuments: (customerId: string, employeeRecordId: string) => dispatchProps.getDocuments(customerId, employeeRecordId)
});

export const Documents = connect(mapStateToProps, mapDispatchToProps, mergeProps)(DocumentsUnconnected);

export const documentsTab = {
    path: "/documents",
    menuItem: (
        <Menu.Item key='documents'>
            Documents <DocumentsTabLabel />
        </Menu.Item>
    ),
    render: () => <Tab.Pane><Documents /></Tab.Pane>
};
